import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { extractEtiquetteUrlFromContent } from '../utils/colissimoLabel';
import type { FoundObjectStatus } from '../types/foundObject';

type Props = {
  content: string;
  payerName: string;
  outgoing?: boolean;
  foundObjectStatus?: FoundObjectStatus | null;
  onMarkInTransit?: () => Promise<void> | void;
  onMarkToShip?: () => Promise<void> | void;
  statusActionPending?: boolean;
};

export default function DeliveryPaymentReceivedMessageBody({
  content,
  payerName,
  outgoing = false,
  foundObjectStatus = null,
  onMarkInTransit,
  onMarkToShip,
  statusActionPending = false,
}: Props) {
  const [toShipConfirmOpen, setToShipConfirmOpen] = useState(false);
  const [markingOnPrint, setMarkingOnPrint] = useState(false);
  const etiquetteUrl = extractEtiquetteUrlFromContent(content);
  const textColor = outgoing ? 'rgba(255,255,255,0.98)' : 'text.primary';
  const alreadyInTransit = foundObjectStatus === 'in_transit';
  const alreadyClaimed = foundObjectStatus === 'claimed';
  const canMarkToShip = Boolean(onMarkToShip) && alreadyInTransit && !alreadyClaimed;
  const busy = statusActionPending || markingOnPrint;

  const handlePrintClick = async () => {
    if (!etiquetteUrl) return;
    window.open(etiquetteUrl, '_blank', 'noopener,noreferrer');
    if (!onMarkInTransit || alreadyInTransit || alreadyClaimed) return;
    setMarkingOnPrint(true);
    try {
      await onMarkInTransit();
    } catch {
      /* parent */
    } finally {
      setMarkingOnPrint(false);
    }
  };

  const handleConfirmToShip = async () => {
    if (!onMarkToShip) return;
    try {
      await onMarkToShip();
      setToShipConfirmOpen(false);
    } catch {
      /* parent */
    }
  };

  return (
    <Stack spacing={1.5} sx={{ maxWidth: 300 }}>
      <Typography variant="body2" sx={{ lineHeight: 1.5, color: textColor }}>
        <strong>{payerName}</strong> a payé la livraison, vous pouvez imprimer le bon.
      </Typography>
      <Button
        variant="contained"
        size="small"
        disabled={!etiquetteUrl || busy}
        onClick={() => void handlePrintClick()}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 700,
          borderRadius: 99,
          px: 2,
        }}
      >
        {markingOnPrint ? 'Impression…' : 'Imprimer le bon'}
      </Button>
      {canMarkToShip ? (
        <Button
          variant="outlined"
          size="small"
          color="secondary"
          disabled={busy}
          onClick={() => setToShipConfirmOpen(true)}
          sx={{
            alignSelf: 'flex-start',
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: 99,
            px: 2,
          }}
        >
          {statusActionPending ? 'Mise à jour…' : 'Remettre à expédier'}
        </Button>
      ) : null}

      <Dialog open={toShipConfirmOpen} onClose={() => !busy && setToShipConfirmOpen(false)}>
        <DialogTitle>Remettre à expédier</DialogTitle>
        <DialogContent>
          <DialogContentText>Confirmez que cet objet revient au statut « À expédier »</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToShipConfirmOpen(false)} disabled={busy}>
            Annuler
          </Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={() => void handleConfirmToShip()}
            disabled={busy}
          >
            Confirmer
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
